import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AreaChart } from "@mantine/charts";
import {
  Alert,
  Button,
  Center,
  Grid,
  Group,
  NumberInput,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconDownload,
  IconExclamationCircle,
  IconInfoCircleFilled,
  IconNurse,
  IconStethoscope,
  IconUserSearch,
  IconUsersGroup,
} from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { formatDistanceToNow } from "date-fns";
import en from "date-fns/locale/en-US";
import fr from "date-fns/locale/fr";

import { OverviewCard } from "@pages/AdminDashboard/components/OverviewCard";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { ROLES } from "@constants/index";

import { useGetStaffEngagementTrend } from "@hooks/queries/dashboard/useGetStaffEngagementTrend";
import { useGetStaffPerformance } from "@hooks/queries/dashboard/useGetStaffPerformance";
import { useGetStaffSessions } from "@hooks/queries/dashboard/useGetStaffSessions";
import { useGetTotalUsersByRole } from "@hooks/queries/dashboard/useGetTotalUsersByRole";

import { StaffPerformance, StaffSession } from "@customTypes/dashboard";

import { StyledTable } from "@styles/components/Table.styled";
import { mq } from "@styles/Utilities.styled";

import { DateRangeSelect } from "../components/DateRangeSelect";


const columnHelper = createColumnHelper<any>();
const columnHelper2 = createColumnHelper<Partial<StaffSession>>();

export const Staff = () => {
  const { t, i18n } = useTranslation();

  const [dateRange, setDateRange] = useState<string | null>("last-7-days");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [staffSessionsTableData, setStaffSessionsTableData] = useState<
    Partial<StaffSession>[] | []
  >([]);
  const [staffPerformanceTableData, setStaffPerformanceTableData] = useState<
    Partial<StaffPerformance>[]
  >([]);
  // const [staffPerformanceTableData, setStaffPerformanceTableData] = useState<Partial<StaffSession>[] | []>([]);
  const isMedium = useMediaQuery(`(${mq.medium})`);

  const {
    data: users,
    isLoading: isUsersLoading,
    isSuccess: isUsersSuccess,
    isError: isUsersError,
  } = useGetTotalUsersByRole({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: staffEngagementTrend,
    isLoading: isStaffEngagementTrendLoading,
    isSuccess: isStaffEngagementTrendSuccess,
    isError: isStaffEngagementTrendError,
  } = useGetStaffEngagementTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: staffSessions,
    isLoading: isStaffSessionsLoading,
    isSuccess: isStaffSessionsSuccess,
    isError: isStaffSessionsError,
    fetchStatus: staffSessionsFetchStatus,
  } = useGetStaffSessions({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: staffPerformance,
    isLoading: isStaffPerformanceLoading,
    isSuccess: isStaffPerformanceSuccess,
    isError: isStaffPerformanceError,
    fetchStatus: staffPerformanceFetchStatus,
  } = useGetStaffPerformance({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });

  const staffSessionColumns = useMemo(
    () => [
      columnHelper2.accessor("name", {
        header: t("name"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14} c="black">
                {info.cell.getValue()}
              </Text>
              <Text c="dimmed" fz={13} tt={"capitalize"}>
                {info.row.original.roles
                  ?.map(item => t(item.toLocaleLowerCase()))
                  .join(", ")}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper2.accessor("lastLogin", {
        header: t("last-login"), // Should be "Last recorded login"
        cell: info => (
          <Group gap={8} align="start">
            <Stack gap={5}>
              <Text fz={14} c="black">
                {formatDistanceToNow(info.cell.getValue() as Date, {
                  addSuffix: true,
                  // @ts-expect-error - Says type is wrong but looks like it works fine for now
                  locale: i18n.language === "fr" ? fr : en,
                  // locale: i18n.language,
                })}
              </Text>
              <Text c="dimmed" fz={13}>
                {info.cell.getValue()?.toLocaleTimeString(i18n.language, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Text>
            </Stack>
          </Group>
        ),
      }),
      columnHelper2.accessor("totalSessions", {
        header: t("total-sessions"),
      }),
      columnHelper2.accessor("avgSessionDuration", {
        header: t("avg-session-duration"),
        cell: info =>
          ` ${Math.floor((info.cell.getValue() ?? 0) / 60)}h ${Math.floor(
            (info.cell.getValue() ?? 0) % 60
          )}m`,
      }),
      columnHelper2.accessor("totalTimeSpent", {
        header: t("total-time-spent"),
        cell: info =>
          ` ${Math.floor((info.cell.getValue() ?? 0) / 60)}h ${Math.floor(
            (info.cell.getValue() ?? 0) % 60
          )}m`,
      }),
    ],
    [staffSessionsTableData, i18n.language]
  );

  const staffPerformanceColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t("name"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14} c="black">
                {info.cell.getValue()}
              </Text>
              <Text c="dimmed" fz={13} tt={"capitalize"}>
                {info.row.original.roles.join(", ")}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor("consultations", {
        header: t("consultations"),
        cell: info => info.cell.getValue() ?? 0,
      }),
      columnHelper.accessor("labsOrdered", {
        header: t("labs-ordered"),
        cell: info => info.cell.getValue() ?? 0,
      }),
      columnHelper.accessor("labsCompleted", {
        header: t("labs-completed"),
        cell: info => info.cell.getValue() ?? "-",
      }),
      columnHelper.accessor("labsAssisted", {
        header: t("labs-assisted"),
        cell: info => info.cell.getValue() ?? "-",
      }),
      columnHelper.accessor("prescriptions", {
        header: t("prescriptions"),
        cell: info => info.cell.getValue() ?? "-",
      }),
      columnHelper.accessor("medications", {
        header: t("medications"),
        cell: info => info.cell.getValue() ?? "-",
      }),
      columnHelper.accessor("earnings", {
        header: t("earnings") + " (CFA)",
        cell: info => info.cell.getValue() ?? "-",
      }),
    ],
    [i18n.language, staffPerformanceTableData]
  );

  const table1 = useReactTable({
    data: staffSessionsTableData || [],
    columns: staffSessionColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const table2 = useReactTable({
    data: staffPerformanceTableData || [],
    columns: staffPerformanceColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    if (staffSessions) {
      setStaffSessionsTableData(staffSessions);
    } else {
      setStaffSessionsTableData([]);
    }
  }, [staffSessions]);

  useEffect(() => {
    if (staffPerformance) {
      setStaffPerformanceTableData(staffPerformance);
    } else {
      setStaffPerformanceTableData([]);
    }
  }, [staffPerformance]);

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={ROLES.ADMIN}>
        <Group mt={"1rem"} justify="start">
          <Text fz={15}>{t("displaying-stats-for")}</Text>
          <DateRangeSelect
            dateRange={dateRange}
            setDateRange={setDateRange}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
          />
        </Group>
        <SimpleGrid cols={{ base: 2, md: 4, lg1: 4 }} mt={"2rem"}>
          <OverviewCard
            title={t("total-staff")}
            amount={users?.total || 0}
            isLoading={isUsersLoading}
            icon={
              <Group
                bg="accent.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconUsersGroup
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-accent-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-doctors")}
            amount={users?.doctors || 0}
            isLoading={isUsersLoading}
            icon={
              <Group
                bg="blue.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconStethoscope
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-blue-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-nurses")}
            amount={users?.nurses || 0}
            isLoading={isUsersLoading}
            icon={
              <Group
                bg="green.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconNurse
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-green-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-receptionists")}
            amount={users?.receptionists || 0}
            isLoading={isUsersLoading}
            icon={
              <Group
                bg="orange.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconUserSearch
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-orange-6)"
                />
              </Group>
            }
          />
        </SimpleGrid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("staff-engagement")}
                </Title>
              </Group>
              {renderLoadingErrorData({
                isLoading: isStaffEngagementTrendLoading,
                isError: isStaffEngagementTrendError,
                isSuccess: isStaffEngagementTrendSuccess,
                dataLength: staffEngagementTrend?.length || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  // <Skeleton h={300} w={"100%"} mt={"1rem"} />
                  <Group h={300} mt={"1rem"} align="end" justify="space-evenly">
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"80%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"20%"} w={"8%"} />
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"70%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"30%"} w={"8%"} />
                    <Skeleton h={"60%"} w={"8%"} />
                    {isMedium && <Skeleton h={"50%"} w={"8%"} />}
                  </Group>
                ),
                dataComponent: (
                  <AreaChart
                    mt={"1rem"}
                    h={300}
                    data={staffEngagementTrend || []}
                    dataKey="date"
                    withLegend
                    tooltipAnimationDuration={150}
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    series={[{ name: "minutes", color: "indigo.5" }]}
                    curveType="linear"
                    tickLine="y"
                    tt={"capitalize"}
                    // yAxisProps={{ domain: [0, 700] }}
                  />
                ),
              })}
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("staff-session-data")}
                </Title>
                <Group gap={"xs"}>
                  {/* <Text>Sort by:</Text>
                  <Select
                    w={150}
                    defaultValue={"Total time spent"}
                    data={["Total time spent", "Ordered", "Completed"]}
                  /> */}
                  <Button leftSection={<IconDownload size={16} />} disabled>
                    {t("export-csv")}
                  </Button>
                </Group>
              </Group>

              <Alert
                mt="1rem"
                variant="light"
                color={"blue"}
                radius={"sm"}
                icon={<IconInfoCircleFilled />}
                title={
                  <Text c={"blue"} fw={500} fz={14} lh={1.5}>
                    {t("heads-up")}
                  </Text>
                }>
                <Text fz={14} lh={1.5} c={"blue"}>
                  {t("telemetry-latency")}
                </Text>
              </Alert>

              <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }} h={"100%"}>
                <StyledTable>
                  <thead>
                    {table1.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <th key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  {isStaffSessionsSuccess && !staffSessions?.length ? (
                    <tbody>
                      <tr className="helper-row">
                        <td colSpan={staffSessionColumns.length}>
                          <p>{t("empty-staff-sessions")}</p>
                        </td>
                      </tr>
                    </tbody>
                  ) : null}

                  {isStaffSessionsLoading &&
                  staffSessionsFetchStatus === "fetching" &&
                  !staffSessionsTableData.length ? (
                    <tbody>
                      <tr className="helper-row">
                        <td colSpan={staffSessionColumns.length}>
                          <p>{t("loading")}</p>
                        </td>
                      </tr>
                    </tbody>
                  ) : null}

                  {isStaffSessionsError && !staffSessions?.length ? (
                    <tbody>
                      <tr className="helper-row">
                        <td colSpan={staffSessionColumns.length}>
                          <p>{t("encountered-error")}</p>
                        </td>
                      </tr>
                    </tbody>
                  ) : null}

                  <tbody>
                    {table1.getRowModel().rows.map(row => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </ScrollArea>
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" mb="8rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("staff-performance")}
                </Title>
                <Group gap={"xs"}>
                  {/* <Text>Sort by:</Text>
                  <Select
                    w={150}
                    defaultValue={"Total time spent"}
                    data={["Total time spent", "Ordered", "Completed"]}
                  /> */}
                  <Button leftSection={<IconDownload size={16} />} disabled>
                    {t("export-csv")}
                  </Button>
                </Group>
              </Group>

              <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }} h={"100%"}>
                <StyledTable>
                  <thead>
                    {table2.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <th key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  {isStaffPerformanceSuccess && !staffPerformance?.length ? (
                    <tbody>
                      <tr className="helper-row">
                        <td colSpan={staffPerformanceColumns.length}>
                          <p>{t("empty-data")}</p>
                        </td>
                      </tr>
                    </tbody>
                  ) : null}

                  {isStaffPerformanceLoading &&
                  staffPerformanceFetchStatus === "fetching" &&
                  !staffPerformanceTableData.length ? (
                    <tbody>
                      <tr className="helper-row">
                        <td colSpan={staffPerformanceColumns.length}>
                          <p>{t("loading")}</p>
                        </td>
                      </tr>
                    </tbody>
                  ) : null}

                  {isStaffPerformanceError && !staffPerformance?.length ? (
                    <tbody>
                      <tr className="helper-row">
                        <td colSpan={staffPerformanceColumns.length}>
                          <p>{t("encountered-error")}</p>
                        </td>
                      </tr>
                    </tbody>
                  ) : null}

                  <tbody>
                    {table2.getRowModel().rows.map(row => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </ScrollArea>
            </Paper>
          </Grid.Col>
        </Grid>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
